// src/pages/Platform/Tabs/Chatbot/ChatbotWidget/ChatbotWidget.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import DOMPurify from 'dompurify';

// Components
import ToggleButton from './ToggleButton';
import ChatHeader from './ChatHeader';
import MessageDisplay from './MessageDisplay';
import ChatInput from './ChatInput';
import ChatMenuInterface from '../ChatbotBuilder/KnowledgeBase/ChatMenu/ChatMenuInterface';

// Hooks
import { useChatMessages } from '../../../../hooks/useChatMessages';
import { useStreamingChat } from '../../../../hooks/useStreamingChat';
import { useChatAnalytics } from '../../../../hooks/useChatAnalytics';
import { useChatConfiguration } from '../../../../hooks/useChatConfiguration';
import { useFormLogic } from '../ChatbotBuilder/KnowledgeBase/Forms';

import './ChatbotWidget.css';

function ChatbotWidget({
  assistantName,
  messagePrompt,
  introMessageText,
  headerColor,
  chatBackgroundColor,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  responseDelay,
  openChatOnLoad,
  chatOpenDelay,
  soundNotificationEnabled = true,
  onChatOpenStateChange,
  isPlatform,
  onSaveConfig,
  showPoweredBy,
  showLinkPreviews = true,
  widgetPosition,
  wooCommerceEnabled,
  orderTrackingEnabled,
  hasCustomAssistant = false,
  hasCustomApiKey = false,
  hideToggleButton = false,
  initiallyOpen = false,
  onClose,
  announcement = {
    text: '',
    enabled: false,
    style: 'info'
  },
  chatbotId: propsChatbotId,
  toggleButtonSize = 'medium',
  toggleButtonIcon = '',
  customToggleButtonIcon = '',
  toggleButtonIconSize,
  toggleButtonBackgroundColor,
  toggleButtonIconOffsetX = 0,
  toggleButtonIconOffsetY = 0,
  toggleButtonOutlineEnabled = false,
  toggleButtonOutlineColor = '#ffffff',
  botTagline = '',
  headerStyle = 'modern',
  botIconUrl = '',
  botIconOffsetX = 0,
  botIconOffsetY = 0,
  menuItems = [],
  humanHandoff = null,
  walkthroughs = [],
  // Preview State Overrides
  forceChatClosed = false,
  forceChatOpen = false,
  forceMenuOpen = false,
  forceShowFirstForm = false,
  // Proactive bubble configuration
  proactiveBubbleEnabled = true,
  proactiveBubbleDelay = 5,
  proactiveBubbleMode = 'ai',
  proactiveBubbleDefaultMessage = 'Have a question? Ask me anything!',
  getPageContext,
  forms = [],
  forceInputDisabled = false,
  ...props
}) {

  // Initialize hooks
  const { updateConfig, notification, showNotification } = useChatConfiguration({
    assistantName, messagePrompt, introMessageText, headerColor,
    chatBackgroundColor, botMessageBoxColor, botMessageTextColor, userMessageBoxColor,
    userMessageTextColor, responseDelay, openChatOnLoad, chatOpenDelay, soundNotificationEnabled, showPoweredBy, showLinkPreviews,
    widgetPosition, announcement, toggleButtonSize, toggleButtonIcon, customToggleButtonIcon,
    toggleButtonIconSize, toggleButtonBackgroundColor, toggleButtonIconOffsetX, toggleButtonIconOffsetY, toggleButtonOutlineEnabled, toggleButtonOutlineColor, wooCommerceEnabled, orderTrackingEnabled,
    botTagline, headerStyle, botIconUrl, botIconOffsetX, botIconOffsetY, menuItems, hasCustomAssistant, hasCustomApiKey
  }, onSaveConfig);

  // Configuration update effect - only run when explicitly needed (platform mode)
  useEffect(() => {
    // Only call updateConfig if we're in platform mode and have an onSaveConfig callback
    if (isPlatform && onSaveConfig) {
      updateConfig();
    }
  }, [isPlatform, onSaveConfig, updateConfig]);

  const { trackUserMessage, trackBotResponse, trackIssueReport } = useChatAnalytics(isPlatform);

  const {
    isCurrentlyStreaming,
    setIsCurrentlyStreaming,
    handleStreamingResponse,
    shouldUseStreaming
  } = useStreamingChat();

  // Chat state
  const [searchParams] = useSearchParams();
  const urlChatbotId = searchParams.get("chatbotId");
  const chatbotId = propsChatbotId || urlChatbotId;

  const threadKey = `threadId_${chatbotId}`;
  const [threadId, setThreadId] = useState(() => localStorage.getItem(threadKey) || null);
  const { messages, setMessages, isSending, setIsSending, saveMessagesToDB, addToSaveQueue } = useChatMessages(introMessageText, threadId, chatbotId);



  const [isChatOpen, setIsChatOpen] = useState(initiallyOpen || false);
  const [inputText, setInputText] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [isQueueActive, setIsQueueActive] = useState(false);

  // Human Handoff Timeout Countdown
  const [handoffCountdown, setHandoffCountdown] = useState(null);
  const countdownIntervalRef = useRef(null);
  const isHandoffRunningRef = useRef(false);

  // Prevent memory leak by clearing countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Preview state overrides
  useEffect(() => {
    if (forceChatClosed) {
      setIsChatOpen(false);
    }
    if (forceChatOpen) {
      setIsChatOpen(true);
    }
  }, [forceChatClosed, forceChatOpen]);

  useEffect(() => {
    if (forceMenuOpen || forceShowFirstForm) {
      // If forcing menu or form, typically means we want to see the chat
      setIsChatOpen(true);
    }
    if (forceMenuOpen) {
      setShowMenu(true);
      // "Chat cleared only menu shown"
      setMessages([]);
      // We might want to keep the intro message though? User said "Chat cleared".
      // If messages are empty, MessageDisplay typically handles empty state nicely or just shows nothing.
    }
  }, [forceMenuOpen, forceShowFirstForm, setMessages]);

  // Unread message notification state
  const [unreadCount, setUnreadCount] = useState(0);
  const previousMessageCountRef = useRef(0);
  const initialLoadRef = useRef(true); // Track if this is the initial page load

  // Human-in-the-loop v2: State-driven conversation mode
  // AI_MODE = AI responds, no polling needed
  // HUMAN_MODE = Human agent handling, poll for responses
  const [conversationMode, setConversationMode] = useState('AI_MODE');

  // Proactive bubble state
  const [showProactiveBubble, setShowProactiveBubble] = useState(false);
  const [proactiveBubbleMessage, setProactiveBubbleMessage] = useState('');
  const proactiveBubbleDismissedRef = useRef(false);

  // User preference for suggestions (toggle on/off)
  const [userSuggestionsEnabled, setUserSuggestionsEnabled] = useState(() => {
    if (chatbotId) {
      const stored = localStorage.getItem(`smushchat_suggestions_${chatbotId}`);
      return stored !== 'false'; // Default to true if not set
    }
    return true;
  });

  const chatAreaRef = useRef(null);
  const inputRef = useRef(null);
  const reportDataRef = useRef(null); // Store data for pending report

  // Helper function
  const sanitizeResponse = (content) => {
    return content.replace(/【.*?†.*?】/g, '');
  };

  // Helper function to get API base URL
  // In platform mode, use current origin. For embedded widgets, detect from script source.
  const getApiBaseUrl = useCallback(() => {
    if (isPlatform) {
      return window.location.origin;
    }
    // For embedded widgets, detect from script source
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src && scripts[i].src.includes('smushchat-widget.js')) {
        if (scripts[i].src.includes('localhost') || scripts[i].src.includes('127.0.0.1')) {
          return 'http://localhost:3000';
        } else if (scripts[i].src.includes('staging.smushlabs.ai')) {
          return 'https://staging.smushlabs.ai';
        } else if (scripts[i].src.includes('smushlabs.ai')) {
          return 'https://smushlabs.ai';
        } else if (scripts[i].src.includes('workers.dev')) {
          const url = new URL(scripts[i].src);
          return url.origin;
        }
      }
    }

    // Fallback for local development (when component is imported directly)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    }

    return 'https://smushlabs.ai'; // Fallback to production
  }, [isPlatform]);

  // Session Tracking: Report URL changes
  useEffect(() => {
    // We poll for URL changes because pushState/replaceState are hard to intercept reliably from outside
    let lastUrl = null; // Initialize as null to ensure first visit is tracked

    const checkUrl = async () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;

        // Report change
        if (chatbotId) {
          try {
            const baseUrl = getApiBaseUrl();
            // console.log('[SmushChat] Tracking visit:', currentUrl, 'to', baseUrl);
            await fetch(`${baseUrl}/api/tracking/visit`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                chatbotId,
                threadId, // Can be null, backend handles it
                url: currentUrl,
                title: document.title
              })
            });
          } catch (e) {
            console.error('[SmushChat] Tracking failed:', e);
          }
        }
      }
    };

    // Check every 2 seconds
    const interval = setInterval(checkUrl, 2000);

    // Also run once on mount/threadId change to record initial entry
    checkUrl();

    return () => clearInterval(interval);
  }, [chatbotId, threadId, getApiBaseUrl]);

  // Mark messages as seen when chat is open
  const markMessagesAsSeen = useCallback(async () => {
    if (!threadId || !chatbotId) return;

    const now = new Date().toISOString();

    // Update local state with seen_at (optimistic update, persists to localStorage)
    setMessages(prevMessages =>
      prevMessages.map(m =>
        m.role === 'assistant' && !m.seen_at
          ? { ...m, seen_at: now }
          : m
      )
    );

    try {
      const baseUrl = getApiBaseUrl();
      await fetch(`${baseUrl}/api/messages/seen`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId })
      });
    } catch (e) {
      // Ignore errors - local state already updated
    }
  }, [threadId, chatbotId, getApiBaseUrl, setMessages]);

  // Human-in-the-loop v2: Initialize conversation mode from thread status
  useEffect(() => {
    if (!threadId || !chatbotId) return;

    const checkThreadStatus = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/threads/status/${threadId}`);
        if (response.ok) {
          const status = await response.json();
          setConversationMode(status.humanHandling ? 'HUMAN_MODE' : 'AI_MODE');
          if (status.humanHandling) {
            setIsQueueActive(true);

            // Recover countdown on page refresh
            if (status.lastMessageAt) {
              const requestTime = new Date(status.lastMessageAt).getTime();
              const now = Date.now();
              const elapsedSeconds = Math.floor((now - requestTime) / 1000);
              const timeoutSec = humanHandoff?.timeoutSeconds !== undefined ? humanHandoff.timeoutSeconds : 60;
              const remainingSeconds = timeoutSec - elapsedSeconds;

              if (remainingSeconds > 0) {
                setHandoffCountdown(remainingSeconds);
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                }
                countdownIntervalRef.current = setInterval(() => {
                  setHandoffCountdown(prev => {
                    if (prev <= 1) {
                      clearInterval(countdownIntervalRef.current);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              } else {
                setHandoffCountdown(0);
              }
            }
          }
        }
      } catch (err) {
        // Default to AI_MODE on error
        setConversationMode('AI_MODE');
      }
    };

    checkThreadStatus();
  }, [threadId, chatbotId, getApiBaseUrl, humanHandoff?.timeoutSeconds]);

  // Human-in-the-loop v2: Heartbeat check for proactive agent takeover
  // When chat is open and we're in AI_MODE, periodically check if agent took over
  useEffect(() => {
    // Only run heartbeat when chat is open, we have a thread, and we're in AI_MODE
    if (!isChatOpen || !threadId || !chatbotId || conversationMode !== 'AI_MODE') return;

    const heartbeatCheck = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/threads/status/${threadId}`);
        if (response.ok) {
          const status = await response.json();
          if (status.humanHandling) {
            // Agent took over proactively! Switch to HUMAN_MODE
            setConversationMode('HUMAN_MODE');
            setIsQueueActive(true);

            // Fetch latest messages to get agent's response
            const messagesRes = await fetch(`${baseUrl}/api/messages/thread/${threadId}`);
            if (messagesRes.ok) {
              const serverMessages = await messagesRes.json();
              setMessages(serverMessages);
              // Mark the agent's message as seen since chat is open
              setTimeout(() => markMessagesAsSeen(), 100);
            }
          }
        }
      } catch (err) {
        // Heartbeat failed, not critical
      }
    };

    // Run immediately when chat opens to catch any takeover that happened while closed
    heartbeatCheck();

    // Then check every 5 seconds for quick detection of proactive takeover
    const intervalId = setInterval(heartbeatCheck, 5000);

    return () => clearInterval(intervalId);
  }, [isChatOpen, threadId, chatbotId, conversationMode, getApiBaseUrl, setMessages, markMessagesAsSeen, humanHandoff?.timeoutSeconds]);

  // Human-in-the-loop v2: State-driven polling
  // Only poll when in HUMAN_MODE - AI_MODE needs no polling
  useEffect(() => {
    // Only poll if we're in HUMAN_MODE waiting for agent response
    if (conversationMode !== 'HUMAN_MODE' || !threadId || !chatbotId) return;

    const pollForMessages = async () => {
      try {
        const baseUrl = getApiBaseUrl();

        // Fetch both messages and thread status
        const [messagesRes, statusRes] = await Promise.all([
          fetch(`${baseUrl}/api/messages/thread/${threadId}`),
          fetch(`${baseUrl}/api/threads/status/${threadId}`)
        ]);

        if (messagesRes.ok) {
          const serverMessages = await messagesRes.json();
          // ANTI-FLICKER + COLLISION HANDLING:
          // We trust server for historic messages (they have IDs/timestamps/metadata).
          // We trust local state for optimistic messages (they are the ones WITHOUT an ID yet).
          // Strategy: Take all Server Messages + Append any Local Messages that don't have an ID.

          // 1. Identify optimistic messages (no ID)
          // refined strategy: Filter out optimistic messages that match the *end* of the server list
          // This handles the case where an optimistic message has been synced but we don't know its ID yet.

          const serverIds = new Set(serverMessages.map(m => m.id).filter(Boolean));

          // Get the last few server messages to check for recent syncs
          const recentServerMessages = serverMessages.slice(-5);

          const optimisticMessages = messages.filter(m => {
            // If it has an ID, it's not optimistic (or if it's in serverIds, it's already accounted for)
            if (m.id && serverIds.has(m.id)) return false;
            if (m.id) return true; // Has ID but not in server list? Keep it (rare edge case)

            // It has NO ID. Check if it's already in the server list (by content match)
            const isAlreadySynced = recentServerMessages.some(sm =>
              sm.role === m.role &&
              (sm.content === m.content || sm.content.trim() === m.content.trim())
            );

            // SPECIAL CASE FOR HUMAN MODE:
            // If we are in HUMAN_MODE, we might have just sent a message that isn't in server list yet.
            // If it's a user message without ID, and NOT in server list, KEEP IT.
            // The logic above `!isAlreadySynced` actually covers this, BUT:
            // The issue might be that `serverMessages` from `fetch` doesn't have it yet, 
            // so we keep it in optimistic. 
            // The bug "disappears immediately" implies `setMessages` is wiping it. 
            // This happens if `mergedMessages` somehow doesn't include it. 
            // If `!isAlreadySynced` is true, it corresponds to "Keep it". 
            // So if `mergedMessages` excludes it, then `isAlreadySynced` must be TRUE?
            // Or maybe `serverMessages` is overwriting it?

            // Let's ensure we keeping user messages that are actively being sent ?? 
            return !isAlreadySynced;
          });

          // 2. Merge: Server History + Optimistic Buffer
          // We trust server order for history. Optimistic messages are always "newest" locally until synced.
          const mergedMessages = [...serverMessages, ...optimisticMessages].sort((a, b) => {
            const dateA = a.timestamp ? new Date(a.timestamp) : new Date();
            const dateB = b.timestamp ? new Date(b.timestamp) : new Date();
            // If timestamps are equal (or both new/null), presume server messages come first
            if (dateA.getTime() === dateB.getTime()) {
              return 0;
            }
            return dateA - dateB;
          });

          // 3. Compare with current state to avoid infinite render loops
          const currentContent = JSON.stringify(messages.map(m => ({ role: m.role, content: m.content, id: m.id })));
          const newContent = JSON.stringify(mergedMessages.map(m => ({ role: m.role, content: m.content, id: m.id })));

          if (currentContent !== newContent) {
            setMessages(mergedMessages);
            // If we have new messages from the server (likely agent response), turn off queue indicator
            // Simple check: if the last message is from 'assistant', it has an ID, AND it's newer than the thread status time, it's a real reply
            const lastMsg = mergedMessages[mergedMessages.length - 1];
            if (lastMsg && lastMsg.role === 'assistant' && lastMsg.id) {
              // Only disable queue if we have a status snapshot and the message is newer than the last user request
              // Otherwise, old assistant messages (like the greeting) will instantly hide the queue indicator
              if (lastMsg.timestamp && statusRes.ok) {
                // If it's a completely new thread, we can just hide it if an assistant replied 
                // But better to let the AI_MODE switch handle disabling it instead to prevent race conditions
                // setIsQueueActive(false); - REMOVED to prevent premature UI drop. AI_MODE switch handles final closing.
              }
            }

            if (isChatOpen) {
              setTimeout(() => markMessagesAsSeen(), 100);
            }
          }
        }

        // Check if agent released control - switch back to AI_MODE
        if (statusRes.ok) {
          const status = await statusRes.json();
          // Only switch back if thread exists AND humanHandling is explicitly false
          // If thread doesn't exist yet (race condition), we stay in HUMAN_MODE
          if (status.exists && !status.humanHandling) {

            // Final fetch to guarantee we get the timeout message (or agent resolution text) 
            // since it was inserted *right* before human_handling flipped to false
            try {
              const finalMsgRes = await fetch(`${baseUrl}/api/messages/thread/${threadId}`);
              if (finalMsgRes.ok) {
                const finalMessages = await finalMsgRes.json();
                setMessages(finalMessages);
                setTimeout(() => markMessagesAsSeen(), 100);
              }
            } catch (err) { }

            setConversationMode('AI_MODE');
            setIsQueueActive(false);

            // Clear countdown if agent responded or AI took back over
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            setHandoffCountdown(null);
          }
        }
      } catch (err) {
        // Polling failed, will retry next interval
      }
    };

    // Poll every 3 seconds in HUMAN_MODE for faster agent response visibility
    const intervalId = setInterval(pollForMessages, 3000);

    // Also poll immediately when entering HUMAN_MODE
    pollForMessages();

    return () => clearInterval(intervalId);
  }, [conversationMode, threadId, chatbotId, getApiBaseUrl, messages, setMessages, isChatOpen, markMessagesAsSeen]);

  // Forms logic hook
  const {
    activeRequiredForm,
    handleFormSubmit,
    shouldShowMenu,
    resetFormState
  } = useFormLogic(chatbotId, isChatOpen, messages, setMessages, saveMessagesToDB, threadId, setThreadId, setIsSending, hasCustomAssistant, hasCustomApiKey, sanitizeResponse, responseDelay, inputRef, isPlatform, threadKey, forceShowFirstForm, forms);



  // Chat toggle handler
  const toggleChat = () => {
    const nextState = !isChatOpen;
    setIsChatOpen(nextState);

    if (onChatOpenStateChange) {
      onChatOpenStateChange(nextState);
    }

    if (!nextState && hideToggleButton && onClose) {
      onClose();
    }
  };

  // Auto-open chat effect
  useEffect(() => {
    if (openChatOnLoad) {
      if (chatOpenDelay > 0) {
        const timer = setTimeout(() => {
          setIsChatOpen(true);
        }, chatOpenDelay * 1000);
        return () => clearTimeout(timer);
      } else {
        setIsChatOpen(true);
      }
    }
  }, [openChatOnLoad, chatOpenDelay]);

  // Proactive bubble effect - fetch contextual message after delay
  useEffect(() => {
    // Skip if disabled by admin, disabled by user, already dismissed, or chat is open
    if (!proactiveBubbleEnabled || !userSuggestionsEnabled || proactiveBubbleDismissedRef.current || isChatOpen) {
      return;
    }

    const timer = setTimeout(async () => {
      // Skip if chat opened while waiting or user toggled off
      if (isChatOpen || proactiveBubbleDismissedRef.current || !userSuggestionsEnabled) return;

      // Check mode - if 'default', use the static message
      if (proactiveBubbleMode === 'default') {
        setProactiveBubbleMessage(proactiveBubbleDefaultMessage);
        proactiveSuggestedQuestionRef.current = proactiveBubbleDefaultMessage;
        setShowProactiveBubble(true);
        return;
      }

      // AI mode - fetch contextual message
      try {
        // Get page context if available
        const pageContext = getPageContext ? getPageContext() : {
          title: document.title,
          pathname: window.location.pathname,
          url: window.location.href
        };

        // Fetch AI-generated message
        const response = await fetch(`${getApiBaseUrl()}/api/chatbot/proactive-prompt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatbotId,
            pageContext
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.message && !isChatOpen && !proactiveBubbleDismissedRef.current) {
            setProactiveBubbleMessage(data.message);
            // Store the user-centric suggested question (use message as fallback)
            proactiveSuggestedQuestionRef.current = data.suggestedQuestion || data.message;
            setShowProactiveBubble(true);
          }
        }
      } catch (error) {
        // Fallback to default message on error
        setProactiveBubbleMessage(proactiveBubbleDefaultMessage);
        proactiveSuggestedQuestionRef.current = proactiveBubbleDefaultMessage;
        setShowProactiveBubble(true);
      }
    }, (proactiveBubbleDelay || 5) * 1000);

    return () => clearTimeout(timer);
  }, [proactiveBubbleEnabled, proactiveBubbleDelay, proactiveBubbleMode, proactiveBubbleDefaultMessage, userSuggestionsEnabled, isChatOpen, chatbotId, getPageContext, getApiBaseUrl]);

  // Hide bubble when chat opens and reset unread count + mark seen
  useEffect(() => {
    if (isChatOpen) {
      setShowProactiveBubble(false);
      // Reset unread count when chat is opened
      setUnreadCount(0);

      // Mark messages as seen
      markMessagesAsSeen();
    }
  }, [isChatOpen, markMessagesAsSeen]);

  // Track new messages when chat is closed for notification badge
  useEffect(() => {
    // Only track when chat is closed
    if (isChatOpen) {
      // When chat is open, track only completed (non-streaming) messages
      const completedMessages = messages.filter(m => !m.isStreaming);
      const prevCount = previousMessageCountRef.current;
      const currentCount = completedMessages.length;

      // If we have new messages while open, mark them as seen
      if (currentCount > prevCount) {
        const lastMsg = completedMessages[completedMessages.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          markMessagesAsSeen();
        }
      }

      previousMessageCountRef.current = completedMessages.length;
      initialLoadRef.current = false; // Mark initial load complete when chat opens
      return;
    }

    // Count only completed (non-streaming) assistant messages
    const completedMessages = messages.filter(m => !m.isStreaming);
    const currentCount = completedMessages.length;

    // On initial page load, just set baseline - don't trigger unread/sound
    if (initialLoadRef.current) {
      previousMessageCountRef.current = currentCount;
      initialLoadRef.current = false;
      return;
    }

    const prevCount = previousMessageCountRef.current;

    if (currentCount > prevCount) {
      // Find new completed messages
      const newMessages = completedMessages.slice(prevCount);
      // Only count UNSEEN assistant messages (those without seen_at)
      const newUnseenAssistantMessages = newMessages.filter(
        m => m.role === 'assistant' && !m.seen_at
      );

      if (newUnseenAssistantMessages.length > 0) {
        setUnreadCount(prev => prev + newUnseenAssistantMessages.length);
      }
    }

    previousMessageCountRef.current = currentCount;
  }, [messages, isChatOpen, markMessagesAsSeen]);

  // Proactive bubble handlers
  const handleProactiveBubbleDismiss = () => {
    setShowProactiveBubble(false);
    proactiveBubbleDismissedRef.current = true;
  };

  // User toggle for suggestions (persists to localStorage)
  const handleUserSuggestionsToggle = () => {
    const newValue = !userSuggestionsEnabled;
    setUserSuggestionsEnabled(newValue);
    if (chatbotId) {
      localStorage.setItem(`smushchat_suggestions_${chatbotId}`, String(newValue));
    }
    // If turning off, also hide the current bubble
    if (!newValue) {
      setShowProactiveBubble(false);
    } else {
      // If turning back on, reset dismissed state so bubble can show again
      proactiveBubbleDismissedRef.current = false;
    }
  };

  // Ref to store pending proactive message to send after chat opens
  const pendingProactiveMessageRef = useRef(null);
  // Ref to store the user-centric suggested question from API
  const proactiveSuggestedQuestionRef = useRef(null);

  const handleProactiveBubbleAccept = (message) => {
    setShowProactiveBubble(false);
    proactiveBubbleDismissedRef.current = true;
    // Use the user-centric suggested question instead of the bubble message
    const userMessage = proactiveSuggestedQuestionRef.current || message;
    pendingProactiveMessageRef.current = userMessage;
    setIsChatOpen(true);
    if (onChatOpenStateChange) {
      onChatOpenStateChange(true);
    }
  };

  // Effect to send proactive message after chat opens
  useEffect(() => {
    if (isChatOpen && pendingProactiveMessageRef.current) {
      const messageToSend = pendingProactiveMessageRef.current;
      pendingProactiveMessageRef.current = null;

      // Small delay to ensure chat is fully rendered
      setTimeout(() => {
        // Set the input text and trigger form submission
        // The form submission handler will add the user message and get bot response
        setInputText(messageToSend);

        // Need another frame for input to be set before submitting
        setTimeout(() => {
          if (inputRef.current) {
            const form = inputRef.current.closest('form');
            if (form) {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          }
        }, 50);
      }, 100);
    }
  }, [isChatOpen]);

  // Auto-scroll effect
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Magic Wand / Issue Reporting Logic
  useEffect(() => {
    const handleElementSelected = (data) => {
      // Store full data for submission
      reportDataRef.current = data;

      // Pre-fill input with report
      const elementDesc = `${data.tagName}${data.id ? '#' + data.id : ''}`;
      const reportMsg = `I want to report an issue with this element: ${elementDesc}`;

      setInputText(reportMsg);

      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    if (window.SmushChatWidget) {
      window.SmushChatWidget.on('elementSelected', handleElementSelected);
    }

    return () => {
      if (window.SmushChatWidget) {
        window.SmushChatWidget.off('elementSelected', handleElementSelected);
      }
    };
  }, []);

  const handleReportIssue = () => {
    if (window.SmushChatWidget && window.SmushChatWidget.startSelectionMode) {
      window.SmushChatWidget.startSelectionMode();
    } else {
      console.warn('Magic Wand not available in this environment');
    }
  };

  // Message sending handler
  const sendMessage = async (e) => {
    e.preventDefault();
    if (inputText.trim() === '') return;

    const userMessage = { role: 'user', content: inputText };
    const newUpdatedMessages = [...messages, userMessage];
    setMessages(newUpdatedMessages);

    // Track user message
    trackUserMessage(chatbotId, inputText.length);

    // Track for widget analytics (GA4) - only works when embedded
    if (!isPlatform && window.SmushChatAnalytics) {
      window.SmushChatAnalytics.trackMessageSent(chatbotId);
    }

    const currentInputText = inputText;
    setInputText('');
    setIsSending(true);

    // INTERCEPT ISSUE REPORTS
    if (currentInputText.startsWith("I want to report an issue") && reportDataRef.current) {
      // 1. Send to Analytics/Backend
      trackIssueReport(chatbotId, {
        ...reportDataRef.current,
        userMessage: currentInputText,
        timestamp: new Date().toISOString()
      }, getApiBaseUrl());

      // 2. Clear data
      reportDataRef.current = null;

      // 3. Fake AI Response
      setTimeout(() => {
        const botMessage = {
          role: 'assistant',
          content: "Thanks for reporting this! 🛠️\n\nI've sent the details (including the element selector and page context) to our engineering team. We'll look into it."
        };
        setMessages(prev => [...prev, botMessage]);
        setIsSending(false);
        saveMessagesToDB(userMessage, botMessage, threadId, chatbotId);
      }, 600); // Small delay for realism

      return; // STOP here, do not call OpenAI
    }

    // Check for walkthrough triggers BEFORE sending to AI
    if (walkthroughs && walkthroughs.length > 0) {
      const lowerInput = currentInputText.toLowerCase();
      const matchedWalkthrough = walkthroughs.find(w =>
        w.isEnabled && w.triggerPhrases?.some(phrase =>
          lowerInput.includes(phrase.toLowerCase())
        )
      );

      if (matchedWalkthrough) {
        // Try to run the walkthrough via the global widget manager
        if (typeof window !== 'undefined' && window.SmushChatWidget?.runWalkthrough) {
          const triggered = window.SmushChatWidget.runWalkthrough(matchedWalkthrough.id);
          if (triggered) {
            // Walkthrough triggered successfully
          }
        }
        // Continue to get AI response as well (don't return early - user still gets help text)
      }
    }

    // Check WooCommerce order tracking
    if (wooCommerceEnabled && orderTrackingEnabled) {
      try {
        const orderTrackingResponse = await fetch(`${getApiBaseUrl()}/api/chatbot/woocommerce/track-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chatbotId: chatbotId,
            message: currentInputText
          })
        });

        if (orderTrackingResponse.ok) {
          const orderData = await orderTrackingResponse.json();

          if (orderData.isOrderTracking) {
            const botMessage = { role: 'assistant', content: orderData.message };
            setMessages([...newUpdatedMessages, botMessage]);
            setIsSending(false);
            saveMessagesToDB(userMessage, botMessage, threadId, chatbotId);
            return;
          }
        }
      } catch (error) {
        // Silently handle order tracking errors
      }
    }

    // HUMAN-IN-THE-LOOP v2: Direct message bypass
    if (conversationMode === 'HUMAN_MODE') {
      setTimeout(() => {
        setIsSending(false);
        saveMessagesToDB(userMessage, null, threadId, chatbotId);
      }, 600);
      return;
    }

    // Handle regular chat messages
    try {
      let assistantMessage;
      let newThreadId = threadId;

      const shouldStream = shouldUseStreaming(currentInputText, hasCustomAssistant);

      // Check API key availability
      if (false) { // Server-side validation handles API key checks
        assistantMessage = {
          role: 'assistant',
          content: 'API key is missing. Please configure your OpenAI API key in Advanced Settings.',
        };
      } else {
        try {
          if (shouldStream) {

            setIsCurrentlyStreaming(true);
            // Stable identity for this bubble so the React key never changes as it
            // streams, finalises, or later gains a server id — preventing a remount
            // (visual "refresh") when the animation completes.
            const streamClientId = `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const tempMessage = { role: 'assistant', content: '', isStreaming: true, clientId: streamClientId };
            setMessages([...newUpdatedMessages, tempMessage]);
            setIsSending(false);

            const streamResult = await handleStreamingResponse(
              chatbotId,
              currentInputText,
              threadId,
              0,
              setMessages,
              sanitizeResponse,
              chatAreaRef
            );

            setIsCurrentlyStreaming(false);

            // HUMAN-IN-THE-LOOP v2: Server indicates human agent is handling
            if (streamResult.humanHandling) {
              // Switch to HUMAN_MODE - this will activate polling
              setConversationMode('HUMAN_MODE');

              // Add a placeholder message so user knows what's happening
              const waitingMessage = {
                role: 'assistant',
                content: '🙋 A human agent is handling your request. Please wait for their response.',
                isHumanHandling: true
              };
              setMessages(prev => [...prev, waitingMessage]);
              setIsSending(false);
              saveMessagesToDB(userMessage, null, streamResult.threadId || threadId, chatbotId);

              // Update threadId if needed
              if (streamResult.threadId && streamResult.threadId !== threadId) {
                setThreadId(streamResult.threadId);
                localStorage.setItem(threadKey, streamResult.threadId);
              }

              return; // Exit early - polling will handle getting agent responses
            }

            assistantMessage = {
              role: 'assistant',
              content: sanitizeResponse(streamResult.message),
              isStreaming: false
            };

            if (streamResult.threadId) {
              newThreadId = streamResult.threadId;
              if (streamResult.threadId !== threadId) {
                setThreadId(streamResult.threadId);
                localStorage.setItem(threadKey, streamResult.threadId);
              }
            }

          } else {
            // Regular non-streaming request
            const makeRetryableChatRequest = async (message, currentThreadId, retryAttempt = 0) => {
              const chatResponse = await fetch(`${getApiBaseUrl()}/api/chatbot/chat`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  chatbotId: chatbotId,
                  message: message,
                  threadId: currentThreadId,
                  retryAttempt: retryAttempt
                })
              });

              if (!chatResponse.ok) {
                const errorData = await chatResponse.json();
                throw new Error(errorData.error || 'Failed to process message');
              }

              const chatData = await chatResponse.json();

              if (chatData.isRetrying) {
                const tempAssistantMessage = {
                  role: 'assistant',
                  content: sanitizeResponse(chatData.message)
                };
                const tempMessages = [...newUpdatedMessages, tempAssistantMessage];
                setMessages(tempMessages);

                await new Promise(resolve => setTimeout(resolve, 1500));
                return makeRetryableChatRequest(chatData.originalMessage, chatData.threadId, chatData.retryAttempt);
              }

              return chatData;
            };

            const chatData = await makeRetryableChatRequest(currentInputText, threadId);

            // HUMAN-IN-THE-LOOP v2: Server indicates human agent is handling
            if (chatData.humanHandling) {
              // Switch to HUMAN_MODE - this will activate polling
              setConversationMode('HUMAN_MODE');

              // Add a placeholder message so user knows what's happening
              const waitingMessage = {
                role: 'assistant',
                content: '🙋 A human agent is handling your request. Please wait for their response.',
                isHumanHandling: true
              };
              setMessages(prev => [...prev, waitingMessage]);
              setIsSending(false);
              saveMessagesToDB(userMessage, null, chatData.threadId || threadId, chatbotId);

              // Update threadId if needed
              if (chatData.threadId && chatData.threadId !== threadId) {
                setThreadId(chatData.threadId);
                localStorage.setItem(threadKey, chatData.threadId);
              }

              return; // Exit early - polling will handle getting agent responses
            }

            assistantMessage = {
              role: 'assistant',
              content: sanitizeResponse(chatData.message)
            };

            if (chatData.threadId) {
              newThreadId = chatData.threadId;
              if (chatData.threadId !== threadId) {
                setThreadId(chatData.threadId);
                localStorage.setItem(threadKey, chatData.threadId);
              }
            }
          }

        } catch (error) {
          setIsCurrentlyStreaming(false);
          assistantMessage = {
            role: 'assistant',
            content: error.message || 'Sorry, there was an error processing your request. Please try again later.'
          };
        }
      }

      // Update messages and handle response delay
      if (!shouldStream || !hasCustomAssistant) {
        const finalMessages = [...newUpdatedMessages, assistantMessage];

        const updateMessagesAndCleanup = () => {
          setMessages(finalMessages);
          setIsSending(false);
          if (inputRef.current) {
            inputRef.current.focus();
          }
          trackBotResponse(chatbotId, assistantMessage.content.length);
          saveMessagesToDB(userMessage, assistantMessage, newThreadId, chatbotId);
        };

        if (responseDelay > 0) {
          setTimeout(updateMessagesAndCleanup, responseDelay * 1000);
        } else {
          updateMessagesAndCleanup();
        }
      } else {
        // For streaming, just clean up
        setIsSending(false);
        setIsCurrentlyStreaming(false);

        if (assistantMessage) {
          assistantMessage.isStreaming = false;
        }

        if (inputRef.current) {
          inputRef.current.focus();
        }
        saveMessagesToDB(userMessage, assistantMessage, newThreadId, chatbotId);
      }

    } catch (error) {
      setIsCurrentlyStreaming(false);
      const errorMessage = { role: 'assistant', content: 'Sorry, there was an error processing your request. Please try again later.' };
      setMessages([...newUpdatedMessages, errorMessage]);
      setIsSending(false);
    }
  };

  // Handle menu item clicks
  const handleMenuItemClick = (userMessage, assistantMessage, menuItem, needsDelay = false) => {
    const messagesWithUser = [...messages, userMessage];
    setMessages(messagesWithUser);
    setShowMenu(false);

    if (needsDelay) {
      setIsSending(true);
      setTimeout(() => {
        const finalMessages = [...messagesWithUser, assistantMessage];
        setMessages(finalMessages);
        setIsSending(false);
        saveMessagesToDB(userMessage, assistantMessage, threadId, chatbotId);
      }, 1000);
    } else {
      const updatedMessages = [...messagesWithUser, assistantMessage];
      setMessages(updatedMessages);
      saveMessagesToDB(userMessage, assistantMessage, threadId, chatbotId);
    }
  };

  // Handle input changes and slash commands
  const handleInputChange = (e) => {
    const value = e.target.value;

    if (value === '/' && menuItems && menuItems.length > 0) {
      setShowMenu(true);
      setInputText('');

      setTimeout(() => {
        if (chatAreaRef.current) {
          chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
      }, 100);

      return;
    }

    if (showMenu && value.length > 0) {
      setShowMenu(false);
    }

    setInputText(value);
  };

  // Clear chat handler
  const clearChat = () => {
    const currentIntroMessage = introMessageText && introMessageText.trim() !== "" ?
      introMessageText :
      "Hello! How can I help you today?";

    const initialMessages = [
      {
        role: 'assistant',
        content: currentIntroMessage,
        timestamp: new Date().toISOString()
      },
    ];
    setMessages(initialMessages);

    setThreadId(null);
    localStorage.removeItem(threadKey);
    resetFormState();

    const storageKey = threadId ? `chatMessages_${chatbotId}_${threadId}` : `chatMessages_${chatbotId}_default`;
    localStorage.setItem(storageKey, JSON.stringify(initialMessages));

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleTalkToHuman = async () => {
    if (!chatbotId) return;
    if (isHandoffRunningRef.current) return; // Prevent concurrent double-clicks or React StrictMode duplicate fires
    isHandoffRunningRef.current = true;

    // Ensure we have a threadId before making the request
    let currentThreadId = threadId;
    if (!currentThreadId) {
      currentThreadId = uuidv4();
      setThreadId(currentThreadId);
      if (threadKey) {
        localStorage.setItem(threadKey, currentThreadId);
      }
    }

    // Serialize the request to ensure order
    await addToSaveQueue(async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/human-request/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatbotId,
            threadId: currentThreadId,
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          })
        });

        const data = await response.json();

        if (data.success) {
          setConversationMode('HUMAN_MODE');
          // Update status immediately
          setIsQueueActive(true);

          // Start countdown
          const timeoutSec = data.timeoutSeconds || 60;
          setHandoffCountdown(timeoutSec);

          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }

          countdownIntervalRef.current = setInterval(() => {
            setHandoffCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownIntervalRef.current);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

        } else {
          let msg = data.error || "Human agents are currently unavailable.";
          if (data.status === 'offline') {
            msg = data.message || "We are currently closed.";
          }
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: msg
          }]);
        }
      } catch (e) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Failed to connect to human agent. Please try again later."
        }]);
      } finally {
        isHandoffRunningRef.current = false;
      }
    });
  };


  return (
    <>
      {!hideToggleButton && (
        <ToggleButton
          isChatOpen={isChatOpen}
          toggleChat={toggleChat}
          isPlatform={isPlatform}
          chatbotPosition={widgetPosition}
          toggleButtonSize={toggleButtonSize}
          toggleButtonIcon={toggleButtonIcon}
          customToggleButtonIcon={customToggleButtonIcon}
          toggleButtonIconSize={toggleButtonIconSize}
          toggleButtonBackgroundColor={toggleButtonBackgroundColor}
          toggleButtonIconOffsetX={toggleButtonIconOffsetX}
          toggleButtonIconOffsetY={toggleButtonIconOffsetY}
          toggleButtonOutlineEnabled={toggleButtonOutlineEnabled}
          toggleButtonOutlineColor={toggleButtonOutlineColor}
          headerColor={headerColor}
          proactiveBubbleMessage={proactiveBubbleMessage}
          showProactiveBubble={showProactiveBubble}
          onProactiveBubbleDismiss={handleProactiveBubbleDismiss}
          onProactiveBubbleAccept={handleProactiveBubbleAccept}
          userSuggestionsEnabled={userSuggestionsEnabled}
          onSuggestionsToggle={handleUserSuggestionsToggle}
          unreadCount={unreadCount}
          soundNotificationEnabled={soundNotificationEnabled}
        />
      )}

      {isChatOpen && (
        <div
          className={`chatbot-popup ${isPlatform ? 'popup-platform' : 'popup-iframe'} ${widgetPosition || 'right'}`}
          style={{
            backgroundColor: chatBackgroundColor,
          }}
        >
          {notification.show && (
            <div className="chatbot-notification">
              {notification.message}
            </div>
          )}

          <ChatHeader
            headerColor={headerColor}
            headerStyle={headerStyle}
            botIconUrl={botIconUrl}
            botIconOffsetX={botIconOffsetX}
            botIconOffsetY={botIconOffsetY}
            assistantName={assistantName}
            botTagline={botTagline}
            isPlatform={isPlatform}
            onToggleChat={toggleChat}
            onReportIssue={handleReportIssue}
            onClearChat={clearChat}
            messages={messages}
            onNotification={showNotification}
            humanHandoffConfig={humanHandoff}
            onTalkToHuman={handleTalkToHuman}
            suggestionsEnabled={userSuggestionsEnabled}
            onSuggestionsToggle={handleUserSuggestionsToggle}
          />

          <div className="chat-area" ref={chatAreaRef}>
            {announcement?.enabled && announcement?.text && (
              <div className={`chatbot-announcement ${announcement.style}`}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(announcement.text, {
                    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
                    ALLOWED_ATTR: ['href', 'target', 'rel']
                  })
                }}
              />
            )}

            <MessageDisplay
              messages={messages}
              botMessageBoxColor={botMessageBoxColor}
              botMessageTextColor={botMessageTextColor}
              userMessageBoxColor={userMessageBoxColor}
              userMessageTextColor={userMessageTextColor}
              handleFormSubmit={handleFormSubmit}
              showLinkPreviews={showLinkPreviews}
              chatbotId={chatbotId}
            />

            {(shouldShowMenu(showMenu, menuItems) || forceMenuOpen) && (
              <ChatMenuInterface
                menuItems={menuItems}
                onMenuItemClick={handleMenuItemClick}
                botMessageBoxColor={botMessageBoxColor}
                botMessageTextColor={botMessageTextColor}
                userMessageBoxColor={userMessageBoxColor}
                userMessageTextColor={userMessageTextColor}
                chatbotId={chatbotId}
                isPlatform={isPlatform}
                isSlashTriggered={showMenu || forceMenuOpen}
              />
            )}

            {isQueueActive && !isSending && (
              <div className="queue-status-indicator" style={{
                color: '#666',
                fontSize: '12px',
                padding: '8px 16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px'
              }}>
                <div className="flex items-center gap-2 flex-1">
                  <div className="typing-indicator" style={{ color: botMessageBoxColor, margin: 0, padding: 0 }}>
                    <div className="dot" style={{ width: '6px', height: '6px' }}></div>
                    <div className="dot" style={{ width: '6px', height: '6px' }}></div>
                    <div className="dot" style={{ width: '6px', height: '6px' }}></div>
                  </div>
                  <span>Waiting for agent...</span>
                </div>
                {handoffCountdown !== null && handoffCountdown > 0 && (
                  <div className="text-xs font-mono font-medium opacity-60">
                    {String(Math.floor(handoffCountdown / 60)).padStart(2, '0')}:{String(handoffCountdown % 60).padStart(2, '0')}
                  </div>
                )}
              </div>
            )}
          </div>

          {isSending && !isCurrentlyStreaming && (
            <div className="typing-indicator" style={{ color: userMessageBoxColor }}>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          )}

          <ChatInput
            inputText={inputText}
            setInputText={setInputText}
            onSendMessage={sendMessage}
            onInputChange={handleInputChange}
            messagePrompt={messagePrompt}
            isSending={isSending}
            inputRef={inputRef}
            chatBackgroundColor={chatBackgroundColor}
            headerColor={headerColor}
            activeRequiredForm={forceInputDisabled ? null : activeRequiredForm}
            showPoweredBy={showPoweredBy}
            forceDisabled={forceInputDisabled}
          />
        </div>
      )}
    </>
  );
}

export default ChatbotWidget;
